import { DISPLAY_TIME_ZONE } from './constants.js';

/**
 * Calendar days as UK clocks see them. History ranges and the nightly retention job are
 * defined in UK days, whatever time zone the browser or the server runs in.
 */

/** A day on the calendar; month is 1–12. */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export const DAY_MS = 24 * 60 * 60 * 1000;

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hourCycle: 'h23',
});

function ukParts(ms: number) {
  const parts: Record<string, number> = {};
  for (const p of partsFormatter.formatToParts(new Date(ms))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  return parts as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>;
}

/** How far UK clocks are ahead of UTC at this instant: 0 in winter, 1 hour in summer. */
export function ukOffsetMs(ms: number): number {
  const p = ukParts(ms);
  const wholeSeconds = Math.floor(ms / 1000) * 1000;
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - wholeSeconds;
}

/** The date in the UK at this instant. */
export function ukDate(ms: number): CalendarDate {
  const p = ukParts(ms);
  return { year: p.year, month: p.month, day: p.day };
}

/**
 * The instant UK clocks show this date and time. (UK clocks change at 01:00 UTC, so
 * midnight and 03:30 always exist exactly once.)
 */
export function ukTime(date: CalendarDate, hour = 0, minute = 0): number {
  const wall = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
  // The offset at the first guess can differ from the offset at the answer near a clock
  // change; a second pass settles it.
  const first = wall - ukOffsetMs(wall);
  return wall - ukOffsetMs(first);
}

/** Next time UK clocks show hour:minute, strictly after `now`. */
export function nextUkTime(now: number, hour: number, minute: number): number {
  const today = ukDate(now);
  const t = ukTime(today, hour, minute);
  return t > now ? t : ukTime(addDays(today, 1), hour, minute);
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Negative if a is before b, 0 if the same day. */
export function compareDates(a: CalendarDate, b: CalendarDate): number {
  return Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day);
}

/** Whole days from a to b. */
export function daysBetween(a: CalendarDate, b: CalendarDate): number {
  return Math.round(compareDates(b, a) / DAY_MS);
}

/** "2026-10-24", for URLs and file names (never shown as a date in the UI). */
export function isoDate(date: CalendarDate): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
}

export function parseIsoDate(text: string | null | undefined): CalendarDate | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text ?? '');
  if (!m) return null;
  const date = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  // Rejects 31/02 and the like, which Date would quietly roll over.
  const normalised = addDays(date, 0);
  return normalised.month === date.month && normalised.day === date.day ? date : null;
}

/** "24/10/2026" */
export function formatCalendarDate(date: CalendarDate): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.day)}/${pad(date.month)}/${date.year}`;
}
